import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  FileText,
  Headphones,
  Image as ImageIcon,
  Loader2,
  Upload,
  Video as VideoIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { useToast } from "@/hooks/use-toast";
import { useUploadQueue } from "@/contexts/UploadQueueContext";
import { supabase } from "@/integrations/supabase/client";
import { uploadThumbnailToCOS, uploadToCOS } from "@/lib/cos-upload";
import { cn } from "@/lib/utils";
import {
  getAllKnownCategoryOptions,
  normalizeCategoryList,
} from "@/lib/category-config";
import {
  addCustomCategoryOption,
  useCategoryOptions,
} from "@/hooks/useCategoryOptions";

interface VideoUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onSuccess: () => void;
}

const UPLOAD_DRAFT_KEY = "admin:video-upload-draft:v1";

type ProcessingStep = "idle" | "uploading" | "processing" | "done" | "error";

interface VttCue {
  startTime: number;
  endTime: number;
  text: string;
}

const JLPT_LEVELS = ["N5", "N4", "N3", "N2", "N1"] as const;

type JlptLevel = (typeof JLPT_LEVELS)[number];

type UploadDraft = {
  contentKind: "video" | "audio";
  formData: {
    title: string;
    description: string;
    jlpt_level: JlptLevel;
    jlpt_levels: string[];
    category: string;
    categories: string[];
    series_id: string;
    is_premium: boolean;
    is_published: boolean;
    autoDetectLevel: boolean;
  };
};

const defaultFormData: UploadDraft["formData"] = {
  title: "",
  description: "",
  jlpt_level: "N5",
  jlpt_levels: ["N5"],
  category: "conversation",
  categories: ["conversation"],
  series_id: "none",
  is_premium: false,
  is_published: false,
  autoDetectLevel: true,
};

function readUploadDraft(): UploadDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(UPLOAD_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UploadDraft>;
    if (!parsed || typeof parsed !== "object") return null;
    const formData = parsed.formData || {};
    const contentKind = parsed.contentKind === "audio" ? "audio" : "video";
    return {
      contentKind,
      formData: {
        ...defaultFormData,
        ...formData,
        jlpt_level: JLPT_LEVELS.includes(formData.jlpt_level as JlptLevel)
          ? (formData.jlpt_level as JlptLevel)
          : "N5",
        jlpt_levels:
          Array.isArray(formData.jlpt_levels) && formData.jlpt_levels.length > 0
            ? Array.from(new Set(formData.jlpt_levels.filter(Boolean)))
            : ["N5"],
        categories:
          Array.isArray(formData.categories) && formData.categories.length > 0
            ? Array.from(new Set(formData.categories.filter(Boolean)))
            : ["conversation"],
      },
    };
  } catch {
    return null;
  }
}

function writeUploadDraft(draft: UploadDraft) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(UPLOAD_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // ignore storage failures
  }
}

function clearUploadDraft() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(UPLOAD_DRAFT_KEY);
  } catch {
    // ignore storage failures
  }
}

function parseVttTime(timeStr: string): number {
  const [h, m, s] = timeStr.split(":");
  if (s !== undefined) {
    return Number(h) * 3600 + Number(m) * 60 + Number(s.replace(",", "."));
  }

  const [mm, ss] = timeStr.split(":");
  if (ss !== undefined) {
    return Number(mm) * 60 + Number(ss.replace(",", "."));
  }

  return 0;
}

function parseVTT(content: string): VttCue[] {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const cues: VttCue[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line.includes("-->")) {
      i += 1;
      continue;
    }

    const [startStr, endStr] = line.split("-->").map((part) => part.trim());
    const startTime = parseVttTime(startStr);
    const endTime = parseVttTime(endStr);

    i += 1;
    const textLines: string[] = [];
    while (i < lines.length && lines[i].trim()) {
      const row = lines[i].trim();
      if (!/^\d+$/.test(row)) {
        textLines.push(row);
      }
      i += 1;
    }

    if (textLines.length > 0 && Number.isFinite(startTime) && Number.isFinite(endTime)) {
      cues.push({
        startTime,
        endTime,
        text: textLines.join("\n"),
      });
    }

    i += 1;
  }

  return cues;
}

function estimateJlpt(text: string): JlptLevel {
  const clean = text.replace(/\s+/g, "");
  if (!clean) return "N5";

  const kanjiCount = (clean.match(/[\u4e00-\u9fff]/g) || []).length;
  const ratio = kanjiCount / clean.length;

  if (ratio >= 0.35) return "N1";
  if (ratio >= 0.28) return "N2";
  if (ratio >= 0.2) return "N3";
  if (ratio >= 0.12) return "N4";
  return "N5";
}

function getStepLabel(step: ProcessingStep): string {
  switch (step) {
    case "uploading":
      return "上传中...";
    case "processing":
      return "处理中（字幕）...";
    case "done":
      return "已完成";
    case "error":
      return "失败";
    default:
      return "";
  }
}

export default function VideoUploadDialog({
  open,
  onOpenChange,
  userId,
  onSuccess,
}: VideoUploadDialogProps) {
  const { toast } = useToast();
  const { addTask } = useUploadQueue();
  const initialDraft = useMemo(() => readUploadDraft(), []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const audioProbeRef = useRef<HTMLAudioElement>(null);

  const [contentKind, setContentKind] = useState<"video" | "audio">(
    initialDraft?.contentKind ?? "video",
  );
  const [formData, setFormData] = useState(() => ({
    ...(initialDraft?.formData ?? defaultFormData),
    jlpt_level: (initialDraft?.formData?.jlpt_level ?? "N5") as JlptLevel,
    jlpt_levels: initialDraft?.formData?.jlpt_levels ?? ["N5"],
    categories: initialDraft?.formData?.categories ?? ["conversation"],
  }));

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [vttFile, setVttFile] = useState<File | null>(null);
  const [vttCues, setVttCues] = useState<VttCue[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);

  const [step, setStep] = useState<ProcessingStep>("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const [seriesList, setSeriesList] = useState<Array<{ id: string; title: string }>>([]);

  const { data: categoryOptions = getAllKnownCategoryOptions(), refetch: refetchCategoryOptions } =
    useCategoryOptions();

  const isProcessing = step === "uploading" || step === "processing";

  useEffect(() => {
    if (!open) return;

    const fetchSeries = async () => {
      const { data } = await supabase.from("series").select("id,title").order("title");
      setSeriesList(data || []);
    };

    fetchSeries();
  }, [open]);

  const filePreviewUrl = useMemo(() => {
    if (!selectedFile) return null;
    return URL.createObjectURL(selectedFile);
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  useEffect(() => {
    writeUploadDraft({
      contentKind,
      formData: {
        ...formData,
        jlpt_levels: Array.from(new Set(formData.jlpt_levels.filter(Boolean))),
        categories: Array.from(new Set(formData.categories.filter(Boolean))),
      },
    });
  }, [contentKind, formData]);

  const reset = (clearDraftStorage = false) => {
    setContentKind("video");
    setFormData({ ...defaultFormData });
    setSelectedFile(null);
    setVttFile(null);
    setVttCues([]);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setAudioDuration(0);
    setStep("idle");
    setProgress(0);
    setErrorMessage("");
    if (clearDraftStorage) clearUploadDraft();
  };

  const handleCreateCategory = (input: string) => {
    const created = addCustomCategoryOption(input);
    if (!created) return null;

    void refetchCategoryOptions();
    toast({
      title: "已新增分类",
      description: `${created.label} 可立即使用`,
    });
    return created;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (contentKind === "video") {
      const isVideo = file.type.startsWith("video/") || file.name.toLowerCase().endsWith(".m3u8");
      if (!isVideo) {
        toast({ title: "请选择视频文件", variant: "destructive" });
        return;
      }
    } else {
      if (!file.type.startsWith("audio/")) {
        toast({ title: "请选择音频文件", variant: "destructive" });
        return;
      }
    }

    setSelectedFile(file);
    setStep("idle");
    setErrorMessage("");

    if (!formData.title.trim()) {
      setFormData((prev) => ({
        ...prev,
        title: file.name.replace(/\.[^.]+$/, ""),
      }));
    }
  };

  const handleSubtitleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".vtt")) {
      toast({ title: "请选择 .vtt 字幕文件", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || "");
      const cues = parseVTT(content);

      setVttFile(file);
      setVttCues(cues);

      if (formData.autoDetectLevel && cues.length > 0) {
        const level = estimateJlpt(cues.map((cue) => cue.text).join(" "));
        setFormData((prev) => ({
          ...prev,
          jlpt_level: level,
          jlpt_levels: [level],
        }));
      }
    };
    reader.readAsText(file);
  };

  const handleThumbnailSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "请选择图片文件", variant: "destructive" });
      return;
    }

    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const onAudioLoadedMeta = () => {
    const duration = audioProbeRef.current?.duration || 0;
    setAudioDuration(duration);
  };

  const handleUpload = async () => {
    if (!selectedFile || !formData.title.trim()) {
      toast({ title: "请先填写标题并选择文件", variant: "destructive" });
      return;
    }

    const normalizedCategories = normalizeCategoryList(formData.categories);
    const primaryCategory =
      normalizedCategories[0] || (contentKind === "audio" ? "story" : "conversation");
    const normalizedJlpt = Array.from(new Set(formData.jlpt_levels.filter(Boolean)));
    const primaryJlpt = (normalizedJlpt[0] as JlptLevel) || "N5";

    if (contentKind === "video") {
      addTask({
        file: selectedFile,
        thumbnailFile: thumbnailFile || undefined,
        vttFile: vttFile || undefined,
        metadata: {
          title: formData.title.trim(),
          description: formData.description.trim(),
          jlpt_level: primaryJlpt,
          jlpt_levels: normalizedJlpt.length > 0 ? normalizedJlpt : [primaryJlpt],
          category: primaryCategory,
          categories: normalizedCategories.length > 0 ? normalizedCategories : [primaryCategory],
          series_id: formData.series_id,
          is_premium: formData.is_premium,
          is_published: formData.is_published,
          autoDetectLevel: formData.autoDetectLevel,
        },
      });

      toast({
        title: "已加入上传队列",
        description: `${formData.title} 已加入上传任务`,
      });

      onOpenChange(false);
      reset(true);
      onSuccess();
      return;
    }

    if (!vttFile || vttCues.length === 0) {
      toast({
        title: "音频必须配字幕",
        description: "请为音频上传 .vtt 字幕文件",
        variant: "destructive",
      });
      return;
    }

    setStep("uploading");
    setProgress(1);
    setErrorMessage("");

    try {
      const uploadResult = await uploadToCOS({
        file: selectedFile,
        folder: "audios",
        onProgress: (value) => setProgress(Math.min(50, Math.round(value))),
      });

      if (!uploadResult.success || !uploadResult.url) {
        throw new Error(uploadResult.error || "音频上传失败");
      }

      let thumbnailUrl: string | null = null;
      if (thumbnailFile) {
        const thumb = await uploadThumbnailToCOS(thumbnailFile);
        if (thumb.success && thumb.url) {
          thumbnailUrl = thumb.url;
        }
      }

      const duration = Math.max(
        Math.floor(audioDuration || 0),
        Math.floor(vttCues[vttCues.length - 1]?.endTime || 0),
      );

      setProgress(65);

      const { data: created, error: insertError } = await supabase
        .from("audios")
        .insert({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          audio_url: uploadResult.url,
          thumbnail_url: thumbnailUrl,
          jlpt_level: primaryJlpt,
          category: primaryCategory,
          is_premium: formData.is_premium,
          is_published: false,
          created_by: userId,
          duration,
          series_id: formData.series_id === "none" ? null : formData.series_id,
        })
        .select()
        .single();

      if (insertError || !created) {
        throw insertError || new Error("创建音频记录失败");
      }

      setStep("processing");
      setProgress(75);

      const { data: processResult, error: processError } = await supabase.functions.invoke(
        "process-audio-subtitles",
        {
          body: {
            audioId: created.id,
            cues: vttCues,
          },
        },
      );

      if (processError) {
        throw new Error(processError.message || "字幕处理失败");
      }
      if ((processResult as any)?.error) {
        throw new Error((processResult as any).error);
      }

      if (formData.is_published) {
        const { error: publishError } = await supabase
          .from("audios")
          .update({ is_published: true })
          .eq("id", created.id);

        if (publishError) throw publishError;
      }

      setProgress(100);
      setStep("done");

      toast({
        title: "音频上传成功",
        description: `${formData.title} 已上传完成`,
      });

      setTimeout(() => {
        onOpenChange(false);
        reset(true);
        onSuccess();
      }, 600);
    } catch (error) {
      const message = error instanceof Error ? error.message : "上传失败";
      setStep("error");
      setErrorMessage(message);
      toast({
        title: "上传失败",
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>上传内容</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-3">
          {step !== "idle" && (
            <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
              <div className="flex items-center gap-2 text-sm">
                {isProcessing && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                {step === "done" && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                {step === "error" && <AlertCircle className="h-4 w-4 text-destructive" />}
                <span>{getStepLabel(step)}</span>
              </div>
              <Progress value={progress} className="h-2" />
              {errorMessage ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
            </div>
          )}

          <div className="space-y-2">
            <Label>内容类型</Label>
            <Select
              value={contentKind}
              onValueChange={(value: "video" | "audio") => setContentKind(value)}
              disabled={isProcessing}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="video">视频</SelectItem>
                <SelectItem value="audio">音频</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div
            className={cn(
              "cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition",
              selectedFile ? "border-primary bg-primary/5" : "hover:border-primary/60",
              isProcessing && "pointer-events-none opacity-50",
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={contentKind === "video" ? "video/*,.m3u8" : "audio/*"}
              onChange={handleFileSelect}
              disabled={isProcessing}
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                {contentKind === "video" ? (
                  <VideoIcon className="h-4 w-4 text-primary" />
                ) : (
                  <Headphones className="h-4 w-4 text-primary" />
                )}
                <span className="break-all">{selectedFile.name}</span>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                点击选择{contentKind === "video" ? "视频" : "音频"}文件
              </div>
            )}
          </div>

          {filePreviewUrl && contentKind === "audio" ? (
            <audio
              ref={audioProbeRef}
              src={filePreviewUrl}
              className="hidden"
              onLoadedMetadata={onAudioLoadedMeta}
            />
          ) : null}

          <div
            className={cn(
              "cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition",
              vttFile ? "border-emerald-500 bg-emerald-500/5" : "hover:border-emerald-500/60",
              isProcessing && "pointer-events-none opacity-50",
            )}
            onClick={() => subtitleInputRef.current?.click()}
          >
            <input
              ref={subtitleInputRef}
              type="file"
              className="hidden"
              accept=".vtt"
              onChange={handleSubtitleSelect}
              disabled={isProcessing}
            />
            {vttFile ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-emerald-600" />
                <span>
                  {vttFile.name}（{vttCues.length} 条）
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(event) => {
                    event.stopPropagation();
                    setVttFile(null);
                    setVttCues([]);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">视频可选，音频必填（.vtt）</div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              封面图（可选）
            </Label>

            {thumbnailPreview ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img src={thumbnailPreview} alt="thumbnail" className="h-32 w-full object-cover" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2 h-7 w-7 bg-background/80"
                  onClick={() => {
                    setThumbnailFile(null);
                    setThumbnailPreview(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => thumbnailInputRef.current?.click()}
                disabled={isProcessing}
              >
                <Upload className="mr-2 h-4 w-4" />
                上传封面
              </Button>
            )}

            <input
              ref={thumbnailInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleThumbnailSelect}
              disabled={isProcessing}
            />
          </div>

          <div className="space-y-2">
            <Label>标题</Label>
            <Input
              value={formData.title}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  title: event.target.value,
                }))
              }
              disabled={isProcessing}
            />
          </div>

          <div className="space-y-2">
            <Label>简介</Label>
            <Textarea
              rows={3}
              value={formData.description}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              disabled={isProcessing}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>JLPT 等级</Label>
              <MultiSelect
                options={JLPT_LEVELS.map((level) => ({ value: level, label: level }))}
                selected={formData.jlpt_levels}
                onChange={(selected) => {
                  const cleaned = Array.from(new Set(selected.filter(Boolean)));
                  setFormData((prev) => ({
                    ...prev,
                    jlpt_levels: cleaned,
                    jlpt_level: (cleaned[0] as JlptLevel) || "N5",
                  }));
                }}
                placeholder="选择等级"
              />
            </div>

            <div className="space-y-2">
              <Label>分类</Label>
              <MultiSelect
                options={categoryOptions}
                selected={formData.categories}
                onChange={(selected) => {
                  const cleaned = normalizeCategoryList(selected);
                  setFormData((prev) => ({
                    ...prev,
                    categories: cleaned,
                    category: cleaned[0] || "conversation",
                  }));
                }}
                allowCreate
                onCreateOption={handleCreateCategory}
                createText="新建"
                searchPlaceholder="搜索或新建分类"
                placeholder="选择分类"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>系列（可选）</Label>
            <Select
              value={formData.series_id}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, series_id: value }))}
              disabled={isProcessing}
            >
              <SelectTrigger>
                <SelectValue placeholder="不归属系列" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">不归属系列</SelectItem>
                {seriesList.map((series) => (
                  <SelectItem key={series.id} value={series.id}>
                    {series.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_premium}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_premium: checked,
                  }))
                }
                disabled={isProcessing}
              />
              <Label>付费内容</Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_published: checked,
                  }))
                }
                disabled={isProcessing}
              />
              <Label>处理完成后发布</Label>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            {step === "error" ? (
              <Button type="button" variant="outline" className="flex-1" onClick={() => setStep("idle")}>
                重试
              </Button>
            ) : null}
            <Button
              type="button"
              className="flex-1"
              onClick={handleUpload}
              disabled={isProcessing || !selectedFile || !formData.title.trim()}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  处理中...
                </>
              ) : (
                "开始上传"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
